import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import NativeSelect from '@material-ui/core/NativeSelect';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';
import { DateTimePicker, DatePicker } from '@material-ui/pickers';
import { formatISO, isBefore, isEqual, isValid, parseISO, addDays } from 'date-fns';
import MUIDataTable from 'mui-datatables';

import { GET_CARDEXISTS } from '../util/schema';
import CheckboxWithId from './CheckboxWithId.jsx';
import SearchIcon from '../icons/SearchIcon.jsx';
import TableFooterWithFirstLastButtons from './TableFooterWithFirstLastButtons.jsx';

const useStyles = makeStyles((theme) => ({
  withMargins: {
    margin: theme.spacing(1),
  },
  wideField: {
    minWidth: theme.spacing(60),
  },
  engageCardFormatSelect: {
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
    '& select': {
      '&:focus': {
        backgroundColor: 'rgba(0,0,0,0) !important',
      },
    },
    border: '1px solid lightgray',
    height: theme.spacing(7),
    paddingLeft: '0.7em',
  },
  cardFunctionSelect: {
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
    '& select': {
      '&:focus': {
        backgroundColor: 'rgba(0,0,0,0) !important',
      },
    },
    border: '1px solid lightgray',
    height: theme.spacing(7),
    paddingLeft: '0.7em',
  },
  actionButton: {
    margin: theme.spacing(3),
    marginLeft: theme.spacing(-0.5),
  },
  // TODO: 'actionButtons' referenced but not defined
  saveButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  dialogWrapper: {
    minHeight: `90vh !important`,
    maxHeight: `90vh !important`,
  },
  container: {
    padding: '0px !important',
    overflow: 'hidden',
  },
  contentArea: {
    height: '60vh',
    overflowY: 'auto',
    padding: theme.spacing(2),
    width: '100%',
  },
  closeButton: {
    float: 'right',
    marginTop: theme.spacing(-0.9),
  },
  closeIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3.5),
    [theme.breakpoints.down('xs')]: {
      width: theme.spacing(2),
      height: theme.spacing(2),
    },
    color: 'white',
  },
  settingsIcon: {
    margin: theme.spacing(0, 0, 0, 1),
  },
  blueMenu: {
    backgroundColor: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    marginBottom: '0px !important',
  },
  menuButton: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.contrastText,
  },
  activeButton: {
    borderBottom: `1px solid ${theme.palette.secondary.contrastText}`,
  },
  menuButtonGrey: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    color: theme.palette.neutral.dark,
  },
  activeButtonGrey: {
    borderBottom: '1px solid',
  },
  cardInfo: {
    marginBottom: theme.spacing(2),
  },
  leftRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  advancedRightRow: {
    height: '5em !important',
  },
  rightRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: theme.spacing(10.25),
  },
  fieldTab: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1),
    paddingLeft: 0,
  },
  firstColumn: {
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  firstColumnAdvanced: {
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    marginLeft: theme.spacing(1.75),
  },
  secondColumn: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    marginTop: theme.spacing(-1.5),
  },
  vacationStartDate: {
    marginLeft: theme.spacing(-0.9),
  },
  fieldLabel: {
    minWidth: theme.spacing(15),
  },
  cardNumberFieldLabel: {
    minWidth: theme.spacing(15),
    paddingBottom: theme.spacing(2),
  },
  advancedFieldLabelRight: {
    width: 'auto !important',
  },
  fieldLabelRight: {
    width: theme.spacing(30),
    textAlign: 'right',
  },
  hiddenFields: {
    display: 'none',
  },
  dataSearchWrapper: {
    position: 'relative',
  },
  dataSearch: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      right: theme.spacing(5),
      marginTop: theme.spacing(2),
      zIndex: 2,
      top: '0px',
    },
    [`& fieldset`]: {
      borderRadius: 0,
    },
  },
  dataTableSearch: {
    padding: '0',
  },
  dataTable: {
    position: 'relative',
    zIndex: 1,
    boxShadow: 'none',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  },
}));

const CardAddEdit = ({
  visible,
  card,
  dismiss,
  onSubmit,
  allAccessGroups,
  allSingleReaderAccessRights,
  isFirstCard,
  isMobile,
  copyDetail,
}) => {
  const classes = useStyles();

  let isEdit = false;
  let isCopyAll = false;
  let isNew = false;
  let actionType = '';
  let initialValues = null;
  const resetActionType = () => {
    isEdit = false;
    isCopyAll = false;
    isNew = false;
  };

  if (
    Object.keys(card).includes('cardNumber') &&
    copyDetail &&
    copyDetail.cardData &&
    copyDetail.accessData
  ) {
    resetActionType();
    isCopyAll = true;
    actionType = 'copyAll';
    initialValues = { ...card };
    initialValues.cardNumber = '';
  } else if (
    Object.keys(card).includes('cardNumber') &&
    copyDetail &&
    copyDetail.cardData &&
    !copyDetail.accessData
  ) {
    resetActionType();
    isCopyAll = true;
    actionType = 'copyCard';
    initialValues = { ...card };
    initialValues.cardNumber = '';
    initialValues.singleReaderAccessRights = { engageReaders: [] };
    initialValues.accessGroups = [];
  } else if (
    Object.keys(card).includes('cardNumber') &&
    copyDetail &&
    !copyDetail.cardData &&
    copyDetail.accessData
  ) {
    resetActionType();
    isCopyAll = true;
    actionType = 'copyAccess';
    initialValues = { ...card };
    initialValues.cardNumber = '';
    initialValues.active = true;
    initialValues.primary = false;
    initialValues.issueCode = '';
    initialValues.pin = '';
    initialValues.useLimit = null;
    initialValues.vacationStartDate = null;
    initialValues.vacationEndDate = null;
    initialValues.freeAntipassback = false;
    initialValues.antipassbackExempt = false;
    initialValues.doNotUpdateUseCount = false;
  } else if (Object.keys(card).includes('cardNumber') && !copyDetail) {
    resetActionType();
    isEdit = true;
    actionType = 'edit';
    initialValues = { ...card };
  } else {
    resetActionType();
    isNew = true;
    actionType = 'new';
    initialValues = {
      cardNumber: '',
      // description: '', // TODO: uncomment when enabling description field
      active: true,
      primary: false,
      accessGroups: [],
      singleReaderAccessRights: { engageReaders: [] },
      issueCode: '',
      pin: '',
      useLimit: null,
      vacationStartDate: null,
      vacationEndDate: null,
      freeAntipassback: false,
      antipassbackExempt: false,
      doNotUpdateUseCount: false,
    };
  }
  /**
   * Helper function to get the index of an accessGroup based on its id property
   * @param {Number} id id of row to get index of
   */
  const findIndexById = (id, list) => {
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].id === id) {
        return i;
      }
    }
    return -1;
  };

  /**
   * Convert array of ag data to array of indeces. Used to populate the data table
   */
  const getAgSelectedIndeces = () => {
    return initialValues.accessGroups.map((ag) => findIndexById(ag.id, allAccessGroups));
  };

  /**
   * Convert array of singleDoorAccess data to array of indeces. Used to populate the data table
   */
  const getSdrSelectedIndeces = () => {
    if (!initialValues.singleReaderAccessRights.engageReaders) {
      return [];
    }
    return initialValues.singleReaderAccessRights.engageReaders.map((door) =>
      findIndexById(door.id, allSingleReaderAccessRights)
    );
  };

  const defaultEngageCardFormat = 'Standard26Bit';
  const engageCardFormatsThatSupportEngageIssueCode = ['XceedId48X'];
  const supportsEngageIssueCode = (engageCardFormat) => {
    return engageCardFormatsThatSupportEngageIssueCode.includes(engageCardFormat);
  };

  const cardFunctionEnabled = true; // TODO: replace this with actual licensing once that is implemented.
  const [cardNumber, setCardNumber] = useState(initialValues.cardNumber || '');
  const [showCardExistsWarning, setShowCardExistsWarning] = useState(false);
  const [showCardNumberValidationWarning, setShowCardNumberValidationWarning] = useState(false);
  // const [description, setDescription] = useState(initialValues.description || ''); // TODO: uncomment when enabling description field
  const [activationDate, setActivationDate] = useState(
    initialValues.activationDate || formatISO(new Date())
  );
  const [activationEnabled, setActivationEnabled] = useState(
    !!initialValues.activationDate || false
  );
  const [deactivationDate, setDeactivationDate] = useState(
    initialValues.deactivationDate || formatISO(new Date())
  );
  const [deactivationEnabled, setDeactivationEnabled] = useState(
    !!initialValues.deactivationDate || false
  );
  const [deactivationError, setDeactivationError] = useState(false);
  const [engageCardFormat, setEngageCardFormat] = useState(
    initialValues.engageCardFormat || defaultEngageCardFormat
  );
  const [siteCode, setSiteCode] = useState(
    (initialValues.siteCode ? initialValues.siteCode : '0') || '0'
  );
  const [showSiteCodeValidationWarning, setShowSiteCodeValidationWarning] = useState(false);
  const [engageIssueCode, setEngageIssueCode] = useState(initialValues.engageIssueCode || '');
  const [engageIssueCodeEnabled, setEngageIssueCodeEnabled] = useState(
    supportsEngageIssueCode(initialValues.engageCardFormat || defaultEngageCardFormat)
  );
  const [showEngageIssueCodeValidationWarning, setShowEngageIssueCodeValidationWarning] = useState(
    false
  );
  const [cardFunction, setCardFunction] = useState(
    cardFunctionEnabled ? initialValues.cardFunction || 'norm' : 'norm'
  );
  const [active, setActive] = useState(initialValues.active || isFirstCard);
  const [primary, setPrimary] = useState(initialValues.primary || isFirstCard);
  const [useAdaTiming, setUseAdaTiming] = useState(initialValues.useAdaTiming || false);

  const [issueCode, setIssueCode] = useState(initialValues.issueCode || '');
  const [pinEnabled, setPinEnabled] = useState(!!initialValues.pin);
  const [pin, setPin] = useState(initialValues.pin || '');
  const [useLimit, setUseLimit] = useState(initialValues.useLimit || '');
  const [useLimitEnabled, setUseLimitEnabled] = useState(!!initialValues.useLimit);
  const [vacationStartDate, setVacationStartDate] = useState(initialValues.vacationStartDate);
  const [vacationStartDateEnabled, setVacationStartDateEnabled] = useState(
    !!initialValues.vacationStartDate
  );
  const [vacationEndDate, setVacationEndDate] = useState(initialValues.vacationEndDate);
  const [vacationEndDateEnabled, setVacationEndDateEnabled] = useState(
    !!initialValues.vacationEndDate
  );
  const [vacationEndDateError, setVacationEndDateError] = useState(false);

  const [freeAntipassback, setFreeAntipassback] = useState(initialValues.freeAntipassback || false);
  const [antipassbackExempt, setAntipassbackExempt] = useState(
    initialValues.antipassbackExempt || false
  );
  const [doNotUpdateUseCount, setDoNotUpdateUseCount] = useState(
    initialValues.doNotUpdateUseCount || false
  );

  const [accessGroups, setAccessGroups] = useState(initialValues.accessGroups || []);
  const initialAgRows = initialValues.accessGroups ? getAgSelectedIndeces() : [];
  const [selectedAgRows, setSelectedAgRows] = useState(initialAgRows);

  const [singleReaderAccessRights, setSingleReaderAccessRights] = useState(
    initialValues.singleReaderAccessRights.engageReaders || []
  );
  const initialSdrRows = initialValues.singleReaderAccessRights ? getSdrSelectedIndeces() : [];
  const [selectedSdrRows, setSelectedSdrRows] = useState(initialSdrRows);

  const [tableShown, setTableShown] = useState('ag');
  const [fieldsShown, setFieldsShown] = useState('cardInfo');
  const [error, setError] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [agSearch, setAgSearch] = useState('');
  const [doorSearch, setDoorSearch] = useState('');

  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const [
    checkCardExists,
    { loading: cardExistsLoading, error: cardExistsError, data: cardExistsData },
  ] = useLazyQuery(GET_CARDEXISTS, {
    onCompleted: (data) => {
      setShowCardExistsWarning(data.cardExists);
    },
  });

  const updateCardNumber = async (event) => {
    const validator = new RegExp(/[^0-9]/g);
    setShowCardNumberValidationWarning(validator.test(event.target.value));
    const onlyNums = event.target.value.replace(validator, '');
    setCardNumber(onlyNums);
    checkCardExists({
      variables: { cardNumber: parseInt(onlyNums) },
    });
  };

  // TODO: uncomment when enabling description field
  // const updateDescription = (event) => {
  //   setDescription(event.target.value);
  // };

  const showDateIsBeforeDateError = (date1, date2) => {
    // only compare at the minute level, since seconds are not rendered in the interface.
    date1.setSeconds(0);
    date2.setSeconds(0);
    if (date1 && date2 && isValid(date1) && isValid(date2)) {
      if (isEqual(date1, date2)) {
        return false;
      }
      return isBefore(date1, date2);
    }
    // TODO: Need to return something in this branch
  };

  const showDateIsBeforeEqualDateError = (date1, date2) => {
    // only compare at the minute level, since seconds are not rendered in the interface.
    if (date1 && date2 && isValid(date1) && isValid(date2)) {
      if (isEqual(date1, date2) || isBefore(date1, date2)) {
        return true;
      }
      return false;
    }
    // TODO: Need to return something in this branch
  };

  const handleDeactivationDateError = () => {
    // check the error if not the activation date is more than the deactivation date
    return deactivationError
      ? 'Deactivation date must be equal to or after the activation date.'
      : null;
  };

  const updateActivationDate = (date) => {
    const isoDate = formatISO(date);
    setActivationDate(isoDate);
    if (activationEnabled && deactivationEnabled) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(isoDate))
      );
    }
  };
  const updateDeactivationDate = async (date) => {
    const isoDate = formatISO(date);
    setDeactivationDate(isoDate);
    if (activationEnabled && deactivationEnabled) {
      setDeactivationError(showDateIsBeforeDateError(parseISO(isoDate), parseISO(activationDate)));
    }
  };
  const updateEngageCardFormat = (event) => {
    setEngageCardFormat(event.target.value);
    const supportsIssueCode = supportsEngageIssueCode(event.target.value);
    setEngageIssueCodeEnabled(supportsIssueCode);
    if (!supportsIssueCode) {
      setEngageIssueCode('');
    }
  };
  const updateSiteCode = (event) => {
    const validator = new RegExp(/[^0-9]/g);
    setShowSiteCodeValidationWarning(validator.test(event.target.value));
    const onlyNums = event.target.value.replace(validator, '');
    setSiteCode(onlyNums);
  };
  const updateEngageIssueCode = (event) => {
    const validator = new RegExp(/[^0-9]/g);
    setShowEngageIssueCodeValidationWarning(validator.test(event.target.value));
    const onlyNums = event.target.value.replace(validator, '');
    setEngageIssueCode(onlyNums);
  };
  const updateCardFunction = (event) => {
    setCardFunction(event.target.value);
  };
  const updateActive = (event) => {
    setActive(event.target.checked);
  };
  const updateActivationEnabled = (event) => {
    setActivationEnabled(event.target.checked);
    setActivationDate(formatISO(new Date()));
    if (event.target.checked && deactivationEnabled) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(activationDate))
      );
    } else {
      setDeactivationError(false);
    }
  };
  const updateDeactivationEnabled = (event) => {
    setDeactivationEnabled(event.target.checked);
    setDeactivationDate(formatISO(new Date()));
    if (activationEnabled && event.target.checked) {
      setDeactivationError(
        showDateIsBeforeDateError(parseISO(deactivationDate), parseISO(activationDate))
      );
    } else {
      setDeactivationError(false);
    }
  };
  const updatePrimary = (event) => {
    setPrimary(event.target.checked);
  };
  const updateUseAdaTiming = (event) => {
    setUseAdaTiming(event.target.checked);
  };

  const updateIssueCode = (event) => setIssueCode(event.target.value);
  const updatePinEnabled = (event) => {
    setPinEnabled(event.target.checked);
  };
  const updatePin = (event) => setPin(event.target.value);
  const updateUseLimit = (event) => setUseLimit(event.target.value);
  const updateUseLimitEnabled = (event) => setUseLimitEnabled(event.target.checked);
  const updateVacationStartDate = (date) => {
    const isoDate = formatISO(date);
    setVacationStartDate(isoDate);
    setVacationEndDateError(
      showDateIsBeforeEqualDateError(parseISO(vacationEndDate), parseISO(isoDate))
    );
  };
  const updateVacationStartDateEnabled = (event) =>
    setVacationStartDateEnabled(event.target.checked);
  const updateVacationEndDate = (date) => {
    const isoDate = formatISO(date);
    setVacationEndDate(isoDate);
    setVacationEndDateError(
      showDateIsBeforeEqualDateError(parseISO(isoDate), parseISO(vacationStartDate))
    );
  };
  const updateVacationEndDateEnabled = (event) => setVacationEndDateEnabled(event.target.checked);
  const updateFreeAntipassback = (event) => setFreeAntipassback(event.target.checked);
  const updateAntipassbackExempt = (event) => setAntipassbackExempt(event.target.checked);
  const updatedoNotUpdateUseCount = (event) => setDoNotUpdateUseCount(event.target.checked);

  const updateAgSearch = (event) => setAgSearch(event.target.value);
  const updateDoorSearch = (event) => setDoorSearch(event.target.value);

  /**
   * update 'initialValues' when the card changes
   */
  useEffect(() => {
    if (visible) {
      setCardNumber(initialValues.cardNumber);
      setActive(initialValues.active || isFirstCard);
      setPrimary(initialValues.primary || isFirstCard);
      setAccessGroups(initialValues.accessGroups || []);
      // setDescription(initialValues.description || []); // TODO: uncomment when enabling description field
      setActivationDate(initialValues.activationDate || formatISO(new Date()));
      setDeactivationDate(initialValues.deactivationDate || formatISO(new Date()));

      setIssueCode(initialValues.issueCode || '');
      setPin(initialValues.pin || '');
      setPinEnabled(!!initialValues.pin);
      setUseLimit(initialValues.useLimit || '');
      setUseLimitEnabled(!!initialValues.useLimit);
      setVacationStartDate(initialValues.vacationStartDate || formatISO(new Date()));
      setVacationStartDateEnabled(!!initialValues.vacationStartDate);
      setVacationEndDate(initialValues.vacationEndDate || formatISO(addDays(new Date(), 1)));
      setVacationEndDateEnabled(!!initialValues.vacationEndDate);

      setFreeAntipassback(initialValues.freeAntipassback || false);
      setAntipassbackExempt(initialValues.antipassbackExempt || false);
      setDoNotUpdateUseCount(initialValues.doNotUpdateUseCount || false);

      setActivationEnabled(!!initialValues.activationDate);
      setDeactivationEnabled(!!initialValues.deactivationDate);

      if (initialValues.accessGroups && initialValues.accessGroups !== []) {
        const selectedRowIndeces = getAgSelectedIndeces();
        setSelectedAgRows(selectedRowIndeces);
      }
      if (
        initialValues.singleReaderAccessRights &&
        initialValues.singleReaderAccessRights.engageReaders &&
        initialValues.singleReaderAccessRights.engageReaders !== []
      ) {
        const selectedRowIndeces = getSdrSelectedIndeces();
        setSelectedSdrRows(selectedRowIndeces);
      }
      setEngageCardFormat(initialValues.engageCardFormat || defaultEngageCardFormat);
      setSiteCode((initialValues.siteCode ? initialValues.siteCode : '0') || '0');
      setEngageIssueCode(initialValues.engageIssueCode || '');
      setEngageIssueCodeEnabled(
        supportsEngageIssueCode(initialValues.engageCardFormat || defaultEngageCardFormat)
      );
      setCardFunction(initialValues.cardFunction || 'norm');
      setUseAdaTiming(initialValues.useAdaTiming || false);
      showAG();
      showCardInfo();
    }
  }, [visible]);

  // rows/columns to render in the table
  const agColumns = ['Access Groups'];

  const agData = allAccessGroups.map((ag) => {
    return [ag.name];
  });

  const singleDoorColumns = ['Doors'];
  const singleDoorData = allSingleReaderAccessRights.map((door) => {
    return [door.name];
  });
  // click handler for row selected in the table
  const handleAgSelected = (currentRowSelected, allRowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedAgRows([]);
      setAccessGroups([]);
    }
    const updatedSelectedAgRows = [];
    const selectedGroups = allRowsSelected.map((selectedRow) => {
      updatedSelectedAgRows.push(selectedRow.dataIndex);
      setSelectedAgRows(updatedSelectedAgRows);
      return allAccessGroups[selectedRow.dataIndex];
    });
    setAccessGroups(selectedGroups);
  };
  // click handler for row selected in the table
  const handleSdrSelected = (currentRowSelected, allRowsSelected) => {
    if (allRowsSelected.length === 0) {
      setSelectedSdrRows([]);
      setSingleReaderAccessRights({ engageReaders: [] });
    }
    const updatedSelectedSdrRows = [];
    const selectedSdr = allRowsSelected.map((selectedRow) => {
      updatedSelectedSdrRows.push(selectedRow.dataIndex);
      setSelectedSdrRows(updatedSelectedSdrRows);
      return allSingleReaderAccessRights[selectedRow.dataIndex];
    });
    setSingleReaderAccessRights({ engageReaders: selectedSdr });
  };

  const baseTableOptions = {
    filter: false,
    print: false,
    search: false,
    download: false,
    viewColumns: false,
    showFirstButton: true,
    showLastButton: false,
    rowsPerPage,
    rowsPerPage: 5, // TODO: Remove duplicate key
    rowsPerPageOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage, textLabels) => {
      return (
        <TableFooterWithFirstLastButtons
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          changePage={changePage}
          textLabels={textLabels}
        />
      );
    },
  };
  const agOptions = {
    ...baseTableOptions,
    onRowSelectionChange: handleAgSelected,
    rowsSelected: selectedAgRows,
    searchText: agSearch,
  };
  const sdrOptions = {
    ...baseTableOptions,
    onRowSelectionChange: handleSdrSelected,
    rowsSelected: selectedSdrRows,
    searchText: doorSearch,
  };
  const handleSubmit = (event) => {
    if (!cardNumberError && !vacationEndDateError && !siteCodeError && !engageIssueCodeError) {
      dismiss();
      let pinValue = pin;
      let useLimitValue = useLimit;
      let vacationStartDateValue = vacationStartDate;
      let vacationEndDateValue = vacationEndDate;
      let activationDateValue = activationDate;
      let deactivationDateValue = deactivationDate;
      if (!pinEnabled) {
        pinValue = null;
      }
      if (!useLimitEnabled) {
        useLimitValue = null;
      }
      if (!vacationStartDateEnabled) {
        vacationStartDateValue = null;
      }
      if (!vacationEndDateEnabled) {
        vacationEndDateValue = null;
      }
      if (!activationEnabled) {
        activationDateValue = null;
      }
      if (!deactivationEnabled) {
        deactivationDateValue = null;
      }
      onSubmit({
        cardNumber,
        // description, // TODO: uncomment when enabling description field
        activationDate: activationDateValue,
        deactivationDate: deactivationDateValue,
        engageCardFormat,
        siteCode,
        engageIssueCode,
        cardFunction,
        active,
        primary,
        useAdaTiming,
        accessGroups,
        singleReaderAccessRights,
        issueCode,
        pin: pinValue,
        useLimit: useLimitValue,
        vacationStartDate: vacationStartDateValue,
        vacationEndDate: vacationEndDateValue,
        freeAntipassback,
        antipassbackExempt,
        doNotUpdateUseCount,
        actionType,
      });
    }
  };

  const generateEngageCardFormatOptions = () => {
    const engageCardFormatOptions = [
      { id: 'Standard26Bit', name: 'Standard 26 bit' },
      { id: 'HidCorporate1000035Bit', name: 'HID Corporate 1000 35 bit' },
      { id: 'Identicard37Bit', name: 'IDenticard 37 bit' },
      { id: 'Hid37Bit', name: 'HID 37 bit' },
      { id: 'MiFareCsn', name: 'MiFare CSN' },
      { id: 'IclassCsn', name: 'iClass CSN' },
      { id: 'Dorado34Bit', name: 'Dorado 34 bit' },
      { id: 'H10313QJoined', name: 'H10313Q (HID) - Joined' },
      { id: 'XceedId40X', name: '40-bit XceedID Cardtrax' },
      { id: 'Csn40Bit', name: '40 bit CSN' },
      { id: 'Rbh50Bit', name: 'RBH50 - 32/16' },
      { id: 'Kastle32Bit', name: 'Kastle 32 bit' },
      { id: 'Csn64Bit', name: '64 bit CSN' },
      { id: 'Awid34Bit', name: 'AWID 34 bit' },
      { id: 'Hid37BitNoSiteCode', name: 'HID 37 bit - No Site Code' },
      { id: 'XceedId48X', name: '48-bit XceedID' },
    ];

    return engageCardFormatOptions.map((option) => (
      <option key={option.id} value={option.id}>
        {option.name}
      </option>
    ));
  };

  const generateFunctionOptions = () => {
    const cardFunctionOptions = [
      { id: 'norm', name: 'Normal' },
      { id: 'toggle', name: 'Toggle' },
      { id: 'freeze', name: 'Freeze' },
      { id: 'lockDown', name: 'Lockdown' },
      { id: 'passThru', name: 'Pass Through' },
      { id: 'oneTm', name: 'One Time Use' },
      { id: 'delAlrm', name: 'Delete With Alarm' },
      { id: 'ctMnAux', name: 'Main and Aux Unlock' },
      { id: 'ctAux', name: 'Aux Unlock' },
      { id: 'ctMnAuxPassThru', name: 'Main and Aux Pass Through' },
      { id: 'ctMnAuxToggle', name: 'Main and Aux Toggle' },
      { id: 'updtFrmSvr', name: 'Update From Server' },
      { id: 'ctAuxToggle', name: 'Aux Toggle' },
      { id: 'updtFrmSvrNrm', name: 'Update From Server' },
    ];

    return cardFunctionEnabled
      ? cardFunctionOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))
      : [];
  };
  const showCardInfo = () => {
    setFieldsShown('cardInfo');
  };
  const showAdvanced = () => {
    setFieldsShown('advanced');
  };
  const showSDR = () => {
    setTableShown('sdr');
  };
  const showAG = () => {
    setTableShown('ag');
  };
  let cardNumberError = '';
  if (showCardNumberValidationWarning) {
    cardNumberError = 'Card Number must be a whole number greater than 0';
  } else if (!isEdit && !cardNumber) {
    cardNumberError = 'Card Number Required';
  } else if (!isEdit && showCardExistsWarning) {
    cardNumberError = 'Card number already in use';
  }
  let siteCodeError = '';
  if (showSiteCodeValidationWarning) {
    siteCodeError = 'Site Code must be a whole number greater than 0';
  } else if (!siteCode) {
    siteCodeError = 'Site Code Required';
  }
  let engageIssueCodeError = '';
  if (showEngageIssueCodeValidationWarning) {
    engageIssueCodeError = 'Engage Issue Code must be a whole number greater than 0';
  } else if (engageIssueCodeEnabled && !engageIssueCode) {
    engageIssueCodeError = 'Engage Issue Code Required';
  }

  const formRef = React.useRef();

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={visible}
      className={classes.dialogWrapper}
      onClose={() => {
        dismiss();
      }}
    >
      <form data-testid="cardForm" ref={formRef}>
        <DialogContent className={classes.container}>
          <div className={`${classes.blueMenu} ${classes.cardInfo}`}>
            <Button
              className={`${classes.menuButton} ${
                fieldsShown === 'cardInfo' ? classes.activeButton : ''
              }`}
              onClick={showCardInfo}
            >
              Card Information
            </Button>
            <Button
              className={`${classes.menuButton} ${
                fieldsShown === 'advanced' ? classes.activeButton : ''
              }`}
              onClick={showAdvanced}
            >
              Advanced <SettingsIcon className={classes.settingsIcon} />
            </Button>
            <IconButton
              className={classes.closeButton}
              disableRipple
              onClick={() => {
                dismiss();
              }}
              data-testid="x-button"
            >
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
          <div className={classes.contentArea}>
            {fieldsShown === 'advanced' && (
              <div>
                <Button className={`${classes.menuButtonGrey} ${classes.activeButtonGrey}`}>
                  Card Properties
                </Button>
              </div>
            )}
            <FormGroup>
              <div>
                <div
                  className={`${classes.fieldTab} ${
                    fieldsShown !== 'cardInfo' ? classes.hiddenFields : ''
                  }`}
                >
                  <div className={classes.firstColumn}>
                    <FormControl className={classes.leftRow}>
                      <Typography
                        id="cardNumberLabel"
                        className={
                          !isEdit && !cardNumber ? classes.cardNumberFieldLabel : classes.fieldLabel
                        }
                      >
                        Card Number
                      </Typography>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        onChange={updateCardNumber}
                        value={cardNumber}
                        variant="outlined"
                        disabled={isEdit}
                        error={!isEdit && (showCardExistsWarning || !cardNumber)}
                        helperText={cardNumberError}
                        required
                        inputProps={{
                          'aria-labelledby': 'cardNumberLabel',
                        }}
                      />
                    </FormControl>
                    {/* TODO: uncomment when enabling description field */}
                    {/* <FormControl className={classes.leftRow}>
                    <Typography id="derscriptionLabel" className={classes.fieldLabel}>
                      Description
                    </Typography>
                    <TextField
                      className={`${classes.withMargins} ${classes.wideField}`}
                      onChange={updateDescription}
                      value={description}
                      variant="outlined"
                      inputProps={{
                        'aria-labelledby': 'derscriptionLabel',
                      }}
                    />
                  </FormControl> */}
                    <FormControl className={classes.leftRow}>
                      <Typography id="activationDateLabel" className={classes.fieldLabel}>
                        Activation Date
                      </Typography>
                      <Checkbox
                        checked={activationEnabled}
                        onChange={updateActivationEnabled}
                        name="enableActivation"
                        inputProps={{
                          'aria-label': 'Enable Activation Date',
                        }}
                      />
                      <DateTimePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!activationEnabled}
                        inputVariant="outlined"
                        format="PPPp"
                        value={activationDate}
                        onChange={updateActivationDate}
                        inputProps={{
                          'aria-labelledby': 'activationDateLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="deactivationDateLabel" className={classes.fieldLabel}>
                        Deactivation Date
                      </Typography>
                      <Checkbox
                        checked={deactivationEnabled}
                        onChange={updateDeactivationEnabled}
                        name="enableDeactivation"
                        inputProps={{
                          'aria-label': 'Enable Deactivation Date',
                        }}
                      />
                      <DateTimePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!deactivationEnabled}
                        inputVariant="outlined"
                        format="PPPp"
                        value={deactivationDate}
                        onChange={updateDeactivationDate}
                        error={deactivationError}
                        helperText={handleDeactivationDateError()}
                        inputProps={{
                          'aria-labelledby': 'deactivationDateLabel',
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className={classes.secondColumn}>
                    <div className={classes.rightRow}>
                      <Typography id="activeLabel" className={classes.fieldLabelRight}>
                        Active
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={active}
                          onChange={updateActive}
                          name="active"
                          inputProps={{
                            'aria-labelledby': 'activeLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                    <div className={classes.rightRow}>
                      <Typography id="primaryLabel" className={classes.fieldLabelRight}>
                        Primary
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={primary}
                          onChange={updatePrimary}
                          name="primary"
                          inputProps={{
                            'aria-labelledby': 'primaryLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                    <div className={classes.rightRow}>
                      <Typography id="adaLabel" className={classes.fieldLabelRight}>
                        Use ADA Timing
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={useAdaTiming}
                          onChange={updateUseAdaTiming}
                          name="useAdaTiming"
                          inputProps={{
                            'aria-labelledby': 'adaLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
                <div
                  className={`${classes.fieldTab} ${
                    fieldsShown !== 'advanced' ? classes.hiddenFields : ''
                  }`}
                >
                  <div className={classes.firstColumnAdvanced}>
                    <FormControl className={classes.leftRow}>
                      <div className={classes.fieldLabel}>
                        <Typography id="issueCodeLabel" display="inline">
                          Issue Code
                        </Typography>
                      </div>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        onChange={updateIssueCode}
                        value={issueCode}
                        variant="outlined"
                        inputProps={{
                          'aria-labelledby': 'issueCodeLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <div className={classes.fieldLabel}>
                        <Typography id="pinNumberLabel" display="inline">
                          Pin Number
                        </Typography>
                      </div>
                      <Checkbox
                        checked={pinEnabled}
                        onChange={updatePinEnabled}
                        name="enablePin"
                        inputProps={{
                          'aria-label': 'Enable Pin Number',
                        }}
                      />
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        onChange={updatePin}
                        value={pin}
                        disabled={!pinEnabled}
                        variant="outlined"
                        inputProps={{
                          'aria-labelledby': 'pinNumberLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <div className={classes.fieldLabel}>
                        <Typography id="useLimitLabel" display="inline">
                          Use Limit
                        </Typography>
                      </div>
                      <Checkbox
                        checked={useLimitEnabled}
                        onChange={updateUseLimitEnabled}
                        name="enableUseLimit"
                        inputProps={{
                          'aria-label': 'Enable Use Limit',
                        }}
                      />
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        onChange={updateUseLimit}
                        value={useLimit}
                        disabled={!useLimitEnabled}
                        variant="outlined"
                        required
                        inputProps={{
                          'aria-labelledby': 'useLimitLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="vacationStartDateLabel" className={classes.fieldLabel}>
                        Vacation Start Date
                      </Typography>
                      <Checkbox
                        className={classes.vacationStartDate}
                        checked={vacationStartDateEnabled}
                        onChange={updateVacationStartDateEnabled}
                        name="enableActivation"
                        inputProps={{
                          'aria-label': 'Enable Vacation Start Date',
                        }}
                      />
                      <DatePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!vacationStartDateEnabled}
                        inputVariant="outlined"
                        format="PPP"
                        value={vacationStartDate || new Date()}
                        onChange={updateVacationStartDate}
                        inputProps={{
                          'aria-labelledby': 'vacationStartDateLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="vacationEndDateLabel" className={classes.fieldLabel}>
                        Vacation End Date
                      </Typography>
                      <Checkbox
                        checked={vacationEndDateEnabled}
                        onChange={updateVacationEndDateEnabled}
                        name="enableActivation"
                        inputProps={{
                          'aria-label': 'Enable Vacation End Date',
                        }}
                      />
                      <DatePicker
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!vacationEndDateEnabled}
                        inputVariant="outlined"
                        format="PPP"
                        value={vacationEndDate}
                        onChange={updateVacationEndDate}
                        error={vacationEndDateError}
                        helperText={
                          vacationEndDateError
                            ? 'Vacation end date must be after the vacation start date.'
                            : null
                        }
                        inputProps={{
                          'aria-labelledby': 'vacationEndDateLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="engageCardFormatLabel" className={classes.fieldLabel}>
                        Engage Card Format
                      </Typography>
                      <NativeSelect
                        className={`${classes.withMargins} ${classes.wideField} ${classes.engageCardFormatSelect}`}
                        value={engageCardFormat}
                        onChange={updateEngageCardFormat}
                        inputProps={{
                          'aria-labelledby': 'engageCardFormatLabel',
                        }}
                      >
                        {generateEngageCardFormatOptions()}
                      </NativeSelect>
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="siteCodeLabel" className={classes.fieldLabel}>
                        Site Code
                      </Typography>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        onChange={updateSiteCode}
                        value={siteCode}
                        variant="outlined"
                        error={!siteCode}
                        helperText={siteCodeError}
                        required
                        inputProps={{
                          'aria-labelledby': 'siteCodeLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="engageIssueCodeLabel" className={classes.fieldLabel}>
                        Engage Issue Code
                      </Typography>
                      <TextField
                        className={`${classes.withMargins} ${classes.wideField}`}
                        disabled={!engageIssueCodeEnabled}
                        required={engageIssueCodeEnabled}
                        error={engageIssueCodeEnabled && !engageIssueCode}
                        onChange={updateEngageIssueCode}
                        value={engageIssueCode}
                        variant="outlined"
                        helperText={engageIssueCodeError}
                        inputProps={{
                          'aria-labelledby': 'engageIssueCodeLabel',
                        }}
                      />
                    </FormControl>
                    <FormControl className={classes.leftRow}>
                      <Typography id="cardFunctionLabel" className={classes.fieldLabel}>
                        Card Function
                      </Typography>
                      <NativeSelect
                        className={`${classes.withMargins} ${classes.wideField} ${classes.cardFunctionSelect}`}
                        disabled={!cardFunctionEnabled}
                        value={cardFunction}
                        onChange={updateCardFunction}
                        inputProps={{
                          'aria-labelledby': 'cardFunctionLabel',
                        }}
                      >
                        {generateFunctionOptions()}
                      </NativeSelect>
                    </FormControl>
                  </div>
                  <div className={classes.secondColumn}>
                    <div className={`${classes.rightRow} ${classes.advancedRightRow}`}>
                      <Typography
                        id="freeAntipassbackLabel"
                        className={`${classes.fieldLabelRight} ${classes.advancedFieldLabelRight}`}
                      >
                        One Free Antipassback Pass
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={freeAntipassback}
                          onChange={updateFreeAntipassback}
                          name="oneFreeAntipassbackPass"
                          inputProps={{
                            'aria-labelledby': 'freeAntipassbackLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                    <div className={`${classes.rightRow} ${classes.advancedRightRow}`}>
                      <Typography
                        id="antipassbackExemptLabel"
                        className={`${classes.fieldLabelRight} ${classes.advancedFieldLabelRight}`}
                      >
                        Antipassback Exempt
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={antipassbackExempt}
                          onChange={updateAntipassbackExempt}
                          name="antipassbackExempt"
                          inputProps={{
                            'aria-labelledby': 'antipassbackExemptLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                    <div className={`${classes.rightRow} ${classes.advancedRightRow}`}>
                      <Typography
                        id="doNotUpdateUseCountLabel"
                        className={`${classes.fieldLabelRight} ${classes.advancedFieldLabelRight}`}
                      >
                        Do Not Update Use Count On Controller
                      </Typography>
                      <FormControl>
                        <Checkbox
                          checked={doNotUpdateUseCount}
                          onChange={updatedoNotUpdateUseCount}
                          name="doNotUpdateUseCount"
                          inputProps={{
                            'aria-labelledby': 'doNotUpdateUseCountLabel',
                          }}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
            </FormGroup>
            {fieldsShown === 'cardInfo' && (
              <div className={classes.blueMenu}>
                <Button
                  className={`${classes.menuButton} ${
                    tableShown === 'ag' ? classes.activeButton : ''
                  }`}
                  onClick={showAG}
                >
                  Access Groups
                </Button>
                <Button
                  className={`${classes.menuButton} ${
                    tableShown === 'sdr' ? classes.activeButton : ''
                  }`}
                  onClick={showSDR}
                >
                  Single Door Rights
                </Button>
                <Button className={classes.menuButton}>Resident Access</Button>
              </div>
            )}
            {tableShown === 'ag' && fieldsShown === 'cardInfo' && (
              <div className={classes.dataSearchWrapper}>
                <TextField
                  className={`${classes.wideField} ${classes.dataSearch}`}
                  onChange={updateAgSearch}
                  value={agSearch}
                  variant="outlined"
                  placeholder="Search"
                  size="small"
                  data-testid="agSearch"
                  InputProps={{
                    // className: classes.dataTableSearch,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <MUIDataTable
                  data={agData}
                  columns={agColumns}
                  options={agOptions}
                  components={{ Checkbox: CheckboxWithId }}
                  className={classes.dataTable}
                />
              </div>
            )}
            {tableShown === 'sdr' && fieldsShown === 'cardInfo' && (
              <div className={classes.dataSearchWrapper}>
                <TextField
                  className={`${classes.wideField} ${classes.dataSearch}`}
                  onChange={updateDoorSearch}
                  value={doorSearch}
                  variant="outlined"
                  placeholder="Search"
                  size="small"
                  data-testid="doorSearch"
                  InputProps={{
                    // className: classes.dataTableSearch,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <MUIDataTable
                  data={singleDoorData}
                  columns={singleDoorColumns}
                  options={sdrOptions}
                  components={{ Checkbox: CheckboxWithId }}
                  className={classes.dataTable}
                />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          {(!cardNumber || !siteCode || (engageIssueCodeEnabled && !engageIssueCode)) && (
            <Typography color="error">Missing required fields!</Typography>
          )}

          <div className={classes.actionButtons}>
            <Button
              className={classes.actionButton}
              onClick={() => {
                dismiss();
              }}
            >
              Cancel
            </Button>
            <Button
              className={`${classes.actionButton} ${classes.saveButton}`}
              variant="contained"
              type="button"
              onClick={() => {
                handleSubmit();
                formRef.current.reportValidity();
              }}
            >
              Save
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CardAddEdit;
